import { useCallback, useEffect, useState } from 'react'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import * as api from '../db/api.js'

const CLOSE_MS = 200

/** Normaliza alternate_domains (text JSON | array | string) → array. */
function parseAlternateDomains(val) {
  if (Array.isArray(val)) return val
  if (typeof val === 'string') {
    try { return JSON.parse(val) } catch { return val ? [val] : [] }
  }
  return []
}

/**
 * Modal "Instalação do Código Tracker" por cliente.
 *
 * Recebe o cliente via prop `client` (vindo do botão "Instalar Tracker" da
 * seção Clientes Vinculados) — NÃO depende do seletor de cliente do topo.
 *
 * Exibe domínio, domínios alternativos (editáveis inline) e o snippet HTML.
 * O tracker_key é usado apenas para montar o snippet — não é exibido ao
 * usuário. Domínios alternativos persistem via PATCH /api/clients/{id}.
 */
export default function TrackerInstallModal({ open, client, onClose }) {
  const { toast, refreshAccess } = useApp()
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)
  const [copiedSnippet, setCopiedSnippet] = useState(false)
  const [copyHover, setCopyHover] = useState(false)
  const [isEditingAlts, setIsEditingAlts] = useState(false)
  const [editAltsValue, setEditAltsValue] = useState('')
  const [savingAlts, setSavingAlts] = useState(false)
  // Lista local de domínios alternativos — semeada do cliente, atualizada após
  // salvar, sem depender da propagação do prop via refreshAccess.
  const [alternateDomains, setAlternateDomains] = useState([])

  const trackerKey = client?.tracker_key || ''
  const siteCode = client?.site_code || ''
  const domain = client?.domain || ''

  const snippet = trackerKey
    ? `<script src="https://tracker.titam.ai/tracker.js"
  data-api-key="${trackerKey}"
  data-api-endpoint="https://tracker.titam.ai"
></script>`
    : ''

  const startClose = useCallback(() => {
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      setMounted(false)
      onClose?.()
    }, CLOSE_MS)
  }, [onClose])

  // Sincroniza montagem e reseta estado a cada abertura.
  useEffect(() => {
    if (open) {
      setMounted(true)
      setClosing(false)
      setCopiedSnippet(false)
      setIsEditingAlts(false)
      setEditAltsValue('')
      setAlternateDomains(parseAlternateDomains(client?.alternate_domains))
    }
  }, [open, client])

  // Esc fecha.
  useEffect(() => {
    if (!mounted) return
    const onKey = (e) => e.key === 'Escape' && startClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mounted, startClose])

  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(snippet)
      setCopiedSnippet(true)
      setTimeout(() => setCopiedSnippet(false), 2000)
    } catch {
      toast('Não foi possível copiar.', 'error')
    }
  }

  function startEditAlts() {
    setEditAltsValue(alternateDomains.join('\n'))
    setIsEditingAlts(true)
  }

  function cancelEditAlts() {
    setIsEditingAlts(false)
    setEditAltsValue('')
  }

  async function saveAlts() {
    if (!client?.id) return
    const raw = editAltsValue
      .split(/[\n,]/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0)
    const domains = [...new Set(raw)]

    setSavingAlts(true)
    try {
      await api.updateClient(client.id, { alternate_domains: domains })
      setAlternateDomains(domains)
      await refreshAccess?.()
      toast('Domínios alternativos atualizados.', 'success')
      setIsEditingAlts(false)
    } catch (err) {
      toast(err.message || 'Falha ao salvar domínios.', 'error')
    } finally {
      setSavingAlts(false)
    }
  }

  if (!mounted) return null

  return (
    <div
      className={`sup-overlay${closing ? ' is-closing' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && startClose()}
    >
      <div className="sup-modal" role="dialog" aria-modal="true" style={{ maxWidth: 560 }}>
        <button className="sup-close" onClick={startClose} aria-label="Fechar">
          <Icon n="x" s={16} />
        </button>

        <div className="sup-head">
          <div className="sup-title">Instalação do Código Tracker</div>
          <div className="sup-subtitle">
            {client?.name ? `Cliente: ${client.name}` : 'Snippet de rastreamento deste cliente.'}
          </div>
        </div>

        {!trackerKey ? (
          <div className="empty">
            <div className="empty-desc">
              Este cliente ainda não possui uma tracker key. Aguarde a migração ou atualize o banco de dados.
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 12, margin: '16px 0 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid var(--soft)', fontSize: 14 }}>
                <span style={{ color: 'var(--sec)' }}>Domínio</span>
                <span style={{ fontWeight: 600 }}>{domain || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '11px 0', borderBottom: '1px solid var(--soft)', fontSize: 14 }}>
                <span style={{ color: 'var(--sec)', paddingTop: 2 }}>Domínios alternativos</span>
                <div style={{ flex: 1, marginLeft: 16 }}>
                  {isEditingAlts ? (
                    <>
                      <textarea
                        value={editAltsValue}
                        onChange={(e) => setEditAltsValue(e.target.value)}
                        rows={3}
                        placeholder="dominio1.com&#10;dominio2.com"
                        style={{
                          width: '100%',
                          background: 'var(--soft)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          padding: 10,
                          color: 'var(--text)',
                          fontSize: 13,
                          fontFamily: 'monospace',
                          resize: 'vertical',
                          minHeight: 60,
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-xs" onClick={cancelEditAlts} disabled={savingAlts}>
                          Cancelar
                        </button>
                        <button className="btn btn-black btn-xs" onClick={saveAlts} disabled={savingAlts}>
                          {savingAlts ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                      {alternateDomains.length > 0 ? alternateDomains.map((d) => (
                        <span key={d} style={{ fontWeight: 600, fontSize: 12, background: 'var(--soft)', padding: '2px 8px', borderRadius: 4 }}>{d}</span>
                      )) : <span style={{ color: 'var(--ter)' }}>—</span>}
                      <button
                        className="btn btn-outline btn-xs"
                        onClick={startEditAlts}
                        style={{ marginLeft: 4 }}
                      >
                        Editar
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', fontSize: 14 }}>
                <span style={{ color: 'var(--sec)' }}>Site Code</span>
                <span style={{ fontWeight: 600, fontFamily: 'monospace', background: 'var(--soft)', padding: '2px 8px', borderRadius: 4 }}>{siteCode || '—'}</span>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--sec)', marginBottom: 8 }}>
                Cole no fim do <code>&lt;body&gt;</code> do site (antes de <code>&lt;/body&gt;</code>):
              </div>
              <div style={{ position: 'relative' }}>
                <pre style={{ background: '#1e1e2e', color: '#cdd6f4', padding: 16, borderRadius: 10, fontSize: 13, overflow: 'auto', lineHeight: 1.5, margin: 0 }}>
                  <code>{snippet}</code>
                </pre>
                <button
                  onClick={copySnippet}
                  onMouseEnter={() => setCopyHover(true)}
                  onMouseLeave={() => setCopyHover(false)}
                  style={{
                    position: 'absolute', top: 8, right: 8,
                    padding: '6px 14px', fontSize: 12, fontWeight: 600,
                    borderRadius: 8, cursor: 'pointer', border: '1px solid transparent',
                    transition: 'background .15s, color .15s',
                    background: copiedSnippet ? '#16a34a' : (copyHover ? '#ffffff' : '#cdd6f4'),
                    color: copiedSnippet ? '#fff' : '#1e1e2e',
                  }}
                >
                  {copiedSnippet ? 'Copiado!' : 'Copiar código'}
                </button>
              </div>
            </div>

            <div style={{ fontSize: 13, color: 'var(--ter)', lineHeight: 1.6 }}>
              <strong>Importante:</strong>
              <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                <li>Cole o código no fim do <code>&lt;body&gt;</code> de todas as páginas do site.</li>
                <li>Não inclua <code>data-site-code</code> — o site_code é buscado automaticamente pelo servidor.</li>
                <li>Se a tracker_key for regenerada, atualize o snippet em todos os sites do cliente.</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
